/**
 * @module components/common/SortByPanel
 * @description 排序配置公共 UI — 排序方式 + 指标排序 + 手动拖拽 + 字段排序
 *
 * 纯展示组件，状态由业务 hook（useSeriesSortBy / useXAxisSortBy）注入。
 */

import React from 'react';
import { useIntl } from 'umi';
import classNames from 'classnames';
import QuickDragList from '@/pages/datalab/components/QuickDragList';
import SortType from './SortType';
import CustomMetricSort from './CustomMetricSort';
import DatasetFieldSort from './DatasetFieldSort';
import type { ISortByPanelViewModel } from './types';
import './index.scss';

export type { ISortByPanelViewModel } from './types';
export { SortByEnum } from './SortType';
export type { TSortByEnum } from './SortType';
export { default as SortType } from './SortType';

const SortByPanel: React.FC<ISortByPanelViewModel> = ({
  sortMode,
  hiddenOptions,
  showMetricSort,
  showManualSort,
  showDatasetFieldSort = false,
  metricSortValue,
  metricSortOptions,
  datasetFieldSortValue = [],
  datasetFieldDataTables = [],
  datasetFieldCustomColumns = [],
  manualDragTitle,
  manualDragList,
  onSortTypeChange,
  onMetricSortChange,
  onDatasetFieldSortChange,
  onManualDragChange,
  headerClassName,
  manualDragMaxHeight = 466,
}) => {
  const { formatMessage } = useIntl();

  return (
    <>
      <div
        className={classNames('dbl-chart-setting-row', headerClassName)}
      >
        <span className="dbl-lable-gray-font">
          {formatMessage({ id: 'dbl_chart_sort_by' })}
        </span>
        <SortType
          value={sortMode}
          onChange={onSortTypeChange}
          hiddenOptions={hiddenOptions}
          className="dbl-sort-by-panel__select"
        />
      </div>

      {showMetricSort && (
        <div className="dbl-sort-by-setting">
          <CustomMetricSort
            value={metricSortValue}
            onChange={onMetricSortChange}
            customOptions={metricSortOptions}
          />
        </div>
      )}

      {showManualSort && (
        <div className="dbl-chart-setting-row">
          <QuickDragList
            title={manualDragTitle}
            value={manualDragList}
            onChange={onManualDragChange}
            maxHeight={manualDragMaxHeight}
          />
        </div>
      )}

      {showDatasetFieldSort && onDatasetFieldSortChange && (
        <DatasetFieldSort
          value={datasetFieldSortValue}
          onChange={onDatasetFieldSortChange}
          dataTables={datasetFieldDataTables}
          customColumns={datasetFieldCustomColumns}
        />
      )}
    </>
  );
};

export default React.memo(SortByPanel);
